/**
 * Timeline service for Fehlzeiten feature.
 *
 * Handles all timeline-related operations including:
 * - Fetching student day timeline (lesson schedule with attendance)
 * - Updating attendance logs
 * - Syncing daily log status
 * - Cleaning up orphaned notes
 *
 * @module fehlzeiten/services/timeline
 */

import { supabase } from "../../../../lib/supabase";
import type { ServiceResult } from "../types/services";

// ============================================================================
// Types
// ============================================================================

/**
 * Represents a lesson in a student's day timeline.
 */
export interface TimelineLesson {
  lesson_id: string | null;
  start: string;
  end: string;
  title: string;
  status: string | null;
  notes: string | null;
  /** Klassenbuch entry text for this lesson (if any) */
  diaryEntryText?: string | null;
  /** Combined cancellation flag (lesson cancelled or substitution cancelled) */
  derivedCancelled?: boolean;
  /** Reason for cancellation (from substitution) */
  cancelReason?: string | null;
  /** Name of who recorded the attendance (for detecting attendance on cancelled lessons) */
  attendanceRecordedByName?: string | null;
  /** When the attendance was recorded */
  attendanceRecordedAt?: string | null;
  /** Class absence/excursion ID if student's class is on a trip */
  classAbsenceId?: string | null;
  /** Type of class absence: ausflug, klassenfahrt, sonstiges */
  classAbsenceType?: string | null;
  /** Destination of the class trip */
  classAbsenceDestination?: string | null;
  /** Start time of partial-day excursion (null = full day) */
  classAbsenceStartTime?: string | null;
  /** End time of partial-day excursion (null = full day) */
  classAbsenceEndTime?: string | null;
  /** Whether attendance is required on the trip */
  classAbsenceAttendanceRequired?: boolean;
  /** Daily log presence status (present, late, absent_excused, absent_unexcused) */
  dailyPresenceStatus?: string | null;
  /** Whether trip attendance was recorded via TripAttendanceDialog */
  dailyIsTripAttendance?: boolean;
}

/**
 * Parameters for updating an attendance log.
 */
export interface UpdateAttendanceLogParams {
  studentId: string;
  lessonId: string;
  status: string;
  notes?: string | null;
  latenessMinutes?: number | null;
}

// SyncDailyLogParams is exported from './timelineDailyLogMutations'

// ============================================================================
// Timeline Queries
// ============================================================================

/**
 * Fetches a student's day timeline with lesson schedule and attendance.
 *
 * Uses the `get_student_day_timeline` RPC to get all lessons for a student
 * on a specific date, including their attendance status.
 *
 * NOTE: This function returns TimelineLesson format used by Tagesverlauf.tsx.
 * For the service layer TimelineEntry format, use fetchStudentDayTimeline from queries.ts.
 *
 * @param studentId - Student UUID
 * @param date - Date in YYYY-MM-DD format
 * @returns Array of timeline lessons
 *
 * @example
 * const lessons = await fetchTimelineLessons('student-uuid', '2026-01-23');
 */
export async function fetchTimelineLessons(
  studentId: string,
  date: string,
): Promise<TimelineLesson[]> {
  if (!studentId || !date) {
    return [];
  }

  const { data, error } = await supabase.rpc(
    "get_student_day_timeline" as never,
    {
      p_student_id: studentId,
      p_date: date,
    } as never,
  );

  if (error) {
    console.error("[timeline] Failed to fetch timeline:", error);
    return [];
  }

  const allLessons = (Array.isArray(data) ? data : []).map(
    (row: Record<string, unknown>) => ({
      lesson_id: row.lesson_id as string | null,
      start:
        (row.period_start_time as string) ||
        (row.start_time as string) ||
        (row.start as string) ||
        "",
      end:
        (row.period_end_time as string) ||
        (row.end_time as string) ||
        (row.end as string) ||
        "",
      title:
        (row.subject_name_full as string) ||
        (row.course_name as string) ||
        (row.subject_name as string) ||
        (row.title as string) ||
        "",
      status: row.status as string | null,
      notes: row.notes as string | null,
      derivedCancelled: row.derived_cancelled as boolean | undefined,
      diaryEntryText: row.diary_entry_text as string | null,
      cancelReason: row.substitute_cancel_reason as string | null,
      attendanceRecordedByName: row.attendance_recorded_by_name as
        | string
        | null,
      attendanceRecordedAt: row.attendance_recorded_at as string | null,
      classAbsenceId: row.class_absence_id as string | null,
      classAbsenceType: row.class_absence_type as string | null,
      classAbsenceDestination: row.class_absence_destination as string | null,
      classAbsenceStartTime: row.class_absence_start_time as string | null,
      classAbsenceEndTime: row.class_absence_end_time as string | null,
      classAbsenceAttendanceRequired: row.class_absence_attendance_required as
        | boolean
        | undefined,
      dailyPresenceStatus: row.daily_presence_status as string | null,
      dailyIsTripAttendance: row.daily_is_trip_attendance as
        | boolean
        | undefined,
    }),
  );

  // Include all lessons — cancelled lessons are shown with visual indicators
  // (CancelledLessonBadge) and allow deleting erroneous attendance records
  return allLessons;
}

/**
 * Fetches the school ID for a student.
 *
 * @param studentId - Student UUID
 * @returns School ID or null
 */
export async function fetchStudentSchoolId(
  studentId: string,
): Promise<string | null> {
  const { data, error } = await supabase
    .from("user_profiles" as never)
    .select("school_id")
    .eq("id" as never, studentId as never)
    .single();

  if (error || !data) {
    return null;
  }

  return (data as { school_id?: string }).school_id || null;
}

// ============================================================================
// Attendance Log Mutations
// ============================================================================

/**
 * Updates or creates an attendance log for a student's lesson.
 *
 * If an attendance log already exists for the student/lesson, it will be
 * updated. Otherwise, a new log entry will be created.
 *
 * @param params - Update parameters
 * @returns Service result indicating success or failure
 *
 * @example
 * await updateAttendanceLog({
 *   studentId: 'student-uuid',
 *   lessonId: 'lesson-uuid',
 *   status: 'present',
 * });
 */
export async function updateAttendanceLog(
  params: UpdateAttendanceLogParams,
): Promise<ServiceResult> {
  const { studentId, lessonId, status, notes, latenessMinutes } = params;

  // Get school ID for the student
  const schoolId = await fetchStudentSchoolId(studentId);
  if (!schoolId) {
    return { success: false, error: "School ID not found for student" };
  }

  // Check for existing attendance log
  const { data: existing, error: checkError } = await supabase
    .from("student_attendance_logs" as never)
    .select("id")
    .eq("student_id" as never, studentId as never)
    .eq("lesson_id" as never, lessonId as never)
    .maybeSingle();

  if (checkError) {
    return { success: false, error: checkError.message };
  }

  const updateData: Record<string, unknown> = {
    status,
    timestamp: new Date().toISOString(),
  };

  if (notes !== undefined) {
    updateData.notes = notes;
  }
  if (latenessMinutes !== undefined && latenessMinutes !== null) {
    updateData.lateness_minutes = latenessMinutes;
  }

  if (existing) {
    // Update existing log
    const { error } = await supabase
      .from("student_attendance_logs" as never)
      .update(updateData as never)
      .eq("id" as never, (existing as { id: string }).id as never);

    if (error) {
      return { success: false, error: error.message };
    }
  } else {
    // Create new log
    const { error } = await supabase
      .from("student_attendance_logs" as never)
      .insert({
        student_id: studentId,
        lesson_id: lessonId,
        school_id: schoolId,
        ...updateData,
      } as never);

    if (error) {
      return { success: false, error: error.message };
    }
  }

  return { success: true };
}

/**
 * Updates multiple attendance logs in batch.
 *
 * @param updates - Array of update parameters
 * @returns Service result with count of successful updates
 */
/**
 * Batch updates attendance logs using RPC.
 *
 * @param updates - Array of attendance log updates
 * @returns Service result with counts
 */
export async function updateAttendanceLogsBatch(
  updates: UpdateAttendanceLogParams[],
): Promise<ServiceResult<{ successCount: number; failedCount: number }>> {
  if (updates.length === 0) {
    return { success: true, data: { successCount: 0, failedCount: 0 } };
  }

  // Transform to RPC format
  const rpcUpdates = updates.map((u) => ({
    student_id: u.studentId,
    lesson_id: u.lessonId,
    status: u.status,
    notes: u.notes || null,
    lateness_duration_minutes: u.latenessMinutes || null,
  }));

  const { data, error } = await supabase.rpc(
    "fn_batch_update_attendance_logs" as never,
    {
      p_updates: rpcUpdates,
    } as never,
  );

  if (error) {
    console.error(
      "[timeline] RPC fn_batch_update_attendance_logs failed:",
      error.message,
    );
    return { success: false, error: error.message };
  }

  const result = Array.isArray(data) ? data[0] : data;
  const success = result?.success ?? true;
  const updatedCount = result?.updated_count ?? 0;
  const insertedCount = result?.inserted_count ?? 0;

  if (!success) {
    return { success: false, error: result?.error_message || "Unknown error" };
  }

  return {
    success: true,
    data: { successCount: updatedCount + insertedCount, failedCount: 0 },
  };
}

/**
 * Deletes attendance logs for a specific student and lesson.
 *
 * Used to remove erroneous attendance records (e.g., attendance recorded
 * on a cancelled lesson).
 *
 * @param studentId - Student UUID
 * @param lessonId - Lesson UUID
 * @returns Service result
 *
 * @example
 * await deleteAttendanceLog('student-uuid', 'lesson-uuid');
 */
export async function deleteAttendanceLog(
  studentId: string,
  lessonId: string,
): Promise<ServiceResult> {
  const { error } = await supabase
    .from("student_attendance_logs" as never)
    .delete()
    .eq("student_id" as never, studentId as never)
    .eq("lesson_id" as never, lessonId as never);

  if (error) {
    console.error("[timeline] Failed to delete attendance log:", error.message);
    return { success: false, error: error.message };
  }

  return { success: true };
}

// ============================================================================
// Daily Log Mutations (re-exports from timelineDailyLogMutations.ts)
// ============================================================================

export {
  syncDailyLogStatus,
  reverseCheckIn,
  updateTripAttendance,
  clearDailyLogAbsenceLink,
  deleteSingleDayAbsenceNote,
  cleanupOrphanedAbsenceNotes,
  type SyncDailyLogParams,
} from "./timelineDailyLogMutations";

// ============================================================================
// Timeline Helpers
// ============================================================================

/**
 * Determines the daily log presence status using the backend RPC.
 *
 * Uses fn_determine_daily_status RPC to get status from the last
 * recorded lesson of the day.
 *
 * @param studentId - Student ID
 * @param date - Date in ISO format (YYYY-MM-DD)
 * @returns Promise resolving to normalized daily log status or null
 */
export async function determineDailyStatusRpc(
  studentId: string,
  date: string,
): Promise<string | null> {
  const { data, error } = await supabase.rpc("fn_determine_daily_status", {
    p_student_id: studentId,
    p_date: date,
  });

  if (error) {
    console.error("[determineDailyStatusRpc] RPC error:", error);
    return null;
  }

  return (data as string) || null;
}

/**
 * Determines the daily log presence status from a list of lessons.
 *
 * Takes the status of the last lesson (by end time) to represent
 * the student's end-of-day status.
 *
 * @deprecated Use determineDailyStatusRpc for server-side calculation
 * @param lessons - Array of timeline lessons
 * @returns Normalized daily log status or null
 */
export function determineDailyStatusFromLessons(
  lessons: TimelineLesson[],
): string | null {
  const toMinutes = (t?: string | null) => {
    if (!t) return -1;
    const [h, m] = String(t)
      .slice(0, 5)
      .split(":")
      .map((x) => parseInt(x, 10));
    if (!isFinite(h) || !isFinite(m)) return -1;
    return h * 60 + m;
  };

  const normalizeLessonStatusToDaily = (s: string | null | undefined) => {
    const st = String(s || "").toLowerCase();
    if (st === "present") return "present";
    if (st === "late" || st === "late_excused" || st === "late_unexcused")
      return "late";
    if (st === "absent_unexcused") return "absent_unexcused";
    if (st === "absent_excused") return "absent_excused";
    return null;
  };

  const lastLesson = [...lessons]
    .filter((l) => !!l && !!l.status && l.status !== "nicht_eingetragen")
    .sort((a, b) => {
      const ae = toMinutes(a.end) >= 0 ? toMinutes(a.end) : toMinutes(a.start);
      const be = toMinutes(b.end) >= 0 ? toMinutes(b.end) : toMinutes(b.start);
      return ae - be;
    })
    .pop();

  return normalizeLessonStatusToDaily(lastLesson?.status || null);
}

/**
 * Checks if any lesson has an absent status.
 *
 * @param lessons - Array of timeline lessons
 * @returns True if any lesson is absent
 */
export function hasAnyAbsentLesson(lessons: TimelineLesson[]): boolean {
  return lessons.some(
    (l) => l.status === "absent_excused" || l.status === "absent_unexcused",
  );
}

/**
 * Converts lateness info to a database-ready status string.
 *
 * @param excused - Whether the lateness is excused
 * @returns Status string for database
 */
export function getLatenessDbStatus(excused: boolean): string {
  return excused ? "late_excused" : "late_unexcused";
}

/**
 * Builds a lateness notes string.
 *
 * @param minutes - Number of minutes late
 * @param excused - Whether the lateness is excused
 * @returns Notes string
 */
export function buildLatenessNotes(minutes: number, excused: boolean): string {
  return `${minutes} Min. verspätet (${
    excused ? "entschuldigt" : "unentschuldigt"
  })`;
}
